/**
 * Returns the scope-filtered option sets for campaign form dropdowns.
 * When a permission is specified, only groups that carry that permission contribute
 * to the allowed scope — so "create" dropdowns show only what the user can create in,
 * not everything they can view.
 */
import { useMemo } from 'react';
import { ALL_SUPPORTED_COUNTRIES } from 'src/constants';
import {
  PermissionScopeBusinessLines as ALL_BUSINESS_LINES,
  PermissionScopePublishers as ALL_PUBLISHERS,
} from 'src/store/constants';
import type { PermissionAssignmentDto } from 'src/store/generated/leela-api-generated';
import { useMyPermissions } from './useMyPermissions';

type Permission =
  | 'VIEW_CAMPAIGNS'
  | 'MANAGE_CAMPAIGNS'
  | 'APPROVE_ASSETS'
  | 'APPROVE_WORKFLOWS'
  | 'MANAGE_BLOCKLISTS';

export interface AllowedScope {
  readonly countries: readonly string[];
  readonly publishers: readonly string[];
  readonly businessLines: readonly string[];
  readonly businessUnitIds: readonly string[];
}

export function useAllowedScope (permission?: Permission): AllowedScope & { isLoading: boolean } {
  const { isPlatformAdmin, isBusinessUnitAdmin, businessUnitAdminOf, assignments, isLoading } = useMyPermissions();

  const scope = useMemo((): AllowedScope => {
    // Platform admins see everything regardless of permission filter
    if (isPlatformAdmin) {
      return {
        countries: [...ALL_SUPPORTED_COUNTRIES],
        publishers: [...ALL_PUBLISHERS],
        businessLines: [...ALL_BUSINESS_LINES],
        businessUnitIds: Array.from(new Set(assignments.map(a => a.businessUnitId))),
      };
    }

    // BU admins have full access within their BU — countries from their BU data,
    // all publishers and business lines
    if (isBusinessUnitAdmin) {
      const buCountries = Array.from(new Set(businessUnitAdminOf.flatMap(bu => bu.countries as string[])));
      return {
        countries: buCountries.length > 0 ? buCountries : [...ALL_SUPPORTED_COUNTRIES],
        publishers: [...ALL_PUBLISHERS],
        businessLines: [...ALL_BUSINESS_LINES],
        businessUnitIds: [...businessUnitAdminOf.map(bu => bu.id)],
      };
    }

    // No assignments at all (pre-seeding / no groups exist yet) — show everything
    // to preserve current behavior until CR E seeds groups
    if (assignments.length === 0) {
      return {
        countries: [...ALL_SUPPORTED_COUNTRIES],
        publishers: [...ALL_PUBLISHERS],
        businessLines: [...ALL_BUSINESS_LINES],
        businessUnitIds: [],
      };
    }

    // Filter assignments to only those carrying the requested permission
    const filtered: readonly PermissionAssignmentDto[] = permission
      ? assignments.filter(a => a.permissions?.includes(permission))
      : assignments;

    if (filtered.length === 0) {
      return { countries: [], publishers: [], businessLines: [], businessUnitIds: [] };
    }

    const countries = filtered.some(a => a.countries.length === 0)
      ? [...ALL_SUPPORTED_COUNTRIES]
      : Array.from(new Set(filtered.flatMap(a => a.countries)));

    const publishers = filtered.some(a => a.publishers.length === 0)
      ? [...ALL_PUBLISHERS]
      : Array.from(new Set(filtered.flatMap(a => a.publishers)));

    const businessLines = filtered.some(a => a.businessLines.length === 0)
      ? [...ALL_BUSINESS_LINES]
      : Array.from(new Set(filtered.flatMap(a => a.businessLines)));

    const businessUnitIds = Array.from(new Set(filtered.map(a => a.businessUnitId)));

    return { countries, publishers, businessLines, businessUnitIds };
  }, [isPlatformAdmin, isBusinessUnitAdmin, businessUnitAdminOf, assignments, permission]);

  return { ...scope, isLoading };
}
